import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { HttpTransportType, HubConnectionBuilder, IHttpConnectionOptions } from '@microsoft/signalr';
import { UserSessionService, EnvironmentService } from '../../services';
import { SgSignalRConnection } from './sg-signal-r-connection';
import { sgSignalRRetryPolicy } from './sg-signal-r-retry';

/** Well under the server's 30s ticket lifetime. */
export const CONNECT_TICKET_TIMEOUT_MS = 10000;

const hidden = Symbol();
export interface SgSignalRConnectionStore {
  // We use this hidden symbol to prevent the key to be never, when there is no augmentation available (i.e. cypress not loading libs)
  [hidden]: any;
}

@Injectable()
export class SgSignalR implements OnDestroy {
  store: Record<keyof SgSignalRConnectionStore, SgSignalRConnection> = <any>{};
  private _waitingStore: Record<keyof SgSignalRConnectionStore, Promise<SgSignalRConnection>> = <any>{};

  constructor(
    private userSession: UserSessionService,
    private env: EnvironmentService,
    private zone: NgZone
  ) {}

  status(name: keyof SgSignalRConnectionStore): 'connected' | 'connecting' | 'disconnected' {
    if (this.store[name]) {
      return 'connected';
    } else if (this._waitingStore[name]) {
      return 'connecting';
    } else {
      return 'disconnected';
    }
  }

  connect(name: keyof SgSignalRConnectionStore, options?: IHttpConnectionOptions): Promise<SgSignalRConnection> {
    switch (this.status(name)) {
      case 'connected':
        return Promise.resolve(this.store[name]);
      case 'connecting':
        return this._waitingStore[name];
      case 'disconnected':
        // No up-front token check: the ticket fetch reads the token itself, and a failure there is retried with
        // the connection instead of failing connect() for good.
        return (this._waitingStore[name] = Promise.resolve()
          .then(() => {
            const connectionOptions: IHttpConnectionOptions = {
              // The session access token never goes in the URL: each WebSocket connect (including every reconnect)
              // gets its own one-time connect ticket. WebSockets only, without negotiate, so that is one ticket per
              // connect; SSE / long polling would call the factory on every poll, send and ping.
              accessTokenFactory: () => this.fetchConnectTicket(),
              transport: HttpTransportType.WebSockets,
              skipNegotiation: true,
            };
            // The policy also hands each failed attempt's reason to the connection, for its outage report.
            let sgConnection: SgSignalRConnection | undefined;
            const connection = new HubConnectionBuilder()
              .withUrl(this.env.ws.baseUrl, Object.assign(connectionOptions, options))
              .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: (context) => {
                  sgConnection?.noteRetryReason(context.retryReason);
                  return sgSignalRRetryPolicy.nextRetryDelayInMilliseconds(context);
                },
              })
              .build();
            delete this._waitingStore[name];

            sgConnection = new SgSignalRConnection(connection, {
              schedule: (fn, ms) => this.scheduleOutsideAngular(fn, ms),
              onClosed: () => {
                // A closed connection never reopens; drop it so a later connect() builds a new one.
                if (this.store[name] === sgConnection) {
                  delete this.store[name];
                }
              },
            });
            this.store[name] = sgConnection;
            return sgConnection;
          })
          .catch((err) => {
            delete this._waitingStore[name];
            throw err;
          }));
    }
  }

  disconnect(name: keyof SgSignalRConnectionStore): boolean {
    const store = this.store[name];
    if (store) {
      store.dispose();
      delete this.store[name];
      return true;
    }
    return false;
  }

  ngOnDestroy(): void {
    for (let k of Object.keys(this.store)) {
      this.store[k].dispose();
    }
  }

  /**
   * Exchanges the session access token (sent in the Authorization header, never the URL) for a short-lived,
   * single-use connect ticket. Plain fetch, not HttpClient, so app interceptors can't turn a failure here into
   * an app-wide 401 handling; a failure fails this connection attempt and the retry policy tries again.
   */
  private async fetchConnectTicket(): Promise<string> {
    if (this.userSession.isLoggedOut) {
      // Recognised by the retry policy as final.
      throw new Error('Not Logged In');
    }
    const token = await this.userSession.authAdapter.getToken();
    // A hung request (headers or body) would stall this attempt forever and the backoff would never advance.
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), CONNECT_TICKET_TIMEOUT_MS);
    try {
      const response = await fetch(`${this.env.ws.baseUrl.replace(/\/+$/, '')}/ticket`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
        signal: abort.signal,
      });
      if (!response.ok) {
        throw new Error(`Live notifications ticket request failed with status ${response.status}`);
      }
      const body: { ticket?: string } | null = await response.json().catch(() => null);
      if (abort.signal.aborted) {
        throw new Error('aborted');
      }
      if (!body?.ticket) {
        throw new Error('Live notifications ticket response had no ticket');
      }
      return body.ticket;
    } catch (err) {
      if (abort.signal.aborted) {
        throw new Error(`Live notifications ticket request timed out after ${CONNECT_TICKET_TIMEOUT_MS}ms`);
      }
      throw err;
    } finally {
      clearTimeout(timeout);
    }
  }

  private scheduleOutsideAngular(fn: () => void, ms: number): () => void {
    // Our own timers (first-connect retry, outage report) run outside the Angular zone so they don't keep the app
    // "unstable"; SignalR's reconnect and ping timers don't. The callback re-enters the zone.
    const handle = this.zone.runOutsideAngular(() => setTimeout(() => this.zone.run(fn), ms));
    return () => clearTimeout(handle);
  }
}
